// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  PublicApi,
  ServerConfiguration,
  createConfiguration,
} from '@digitalasset/allocation-api-ts-client';
import {
  PublicApi as AllocationInstructionPublicApi,
  ServerConfiguration as AllocationInstructionServerConfiguration,
  createConfiguration as allocationInstructionCreateConfiguration,
} from '@digitalasset/allocation-instruction-api-ts-client';
import {
  PublicApi as TransferInstructionPublicApi,
  ServerConfiguration as TransferInstructionServerConfiguration,
  createConfiguration as transferInstructionCreateConfiguration,
} from '@digitalasset/transfer-instruction-api-ts-client';
import React, { useMemo } from 'react';
import { AllocationApiContext } from './AllocationApiContext';
import { AllocationInstructionApiContext } from './AllocationInstructionApiContext';
import { TransferInstructionApiContext } from './TransferInstructionApiContext';

interface AllocationApiProps {
  url: string;
}

export const AllocationApiProvider = ({
  url,
  children,
}: React.PropsWithChildren<AllocationApiProps>) => {
  const client: PublicApi | undefined = useMemo(() => {
    const configuration = createConfiguration({
      baseServer: new ServerConfiguration(url, {}),
    });

    return new PublicApi(configuration);
  }, [url]);

  const instructionClient: AllocationInstructionPublicApi | undefined = useMemo(() => {
    const configuration = allocationInstructionCreateConfiguration({
      baseServer: new AllocationInstructionServerConfiguration(url, {}),
    });

    return new AllocationInstructionPublicApi(configuration);
  }, [url]);

  const transferInstructionClient: TransferInstructionPublicApi | undefined = useMemo(() => {
    const configuration = transferInstructionCreateConfiguration({
      baseServer: new TransferInstructionServerConfiguration(url, {}),
    });

    return new TransferInstructionPublicApi(configuration);
  }, [url]);

  return (
    <AllocationApiContext.Provider value={client}>
      <AllocationInstructionApiContext.Provider value={instructionClient}>
        <TransferInstructionApiContext.Provider value={transferInstructionClient}>
          {children}
        </TransferInstructionApiContext.Provider>
      </AllocationInstructionApiContext.Provider>
    </AllocationApiContext.Provider>
  );
};
